/**
 * German Business & Recruitment Lingo Override
 *
 * Google Translate frequently produces awkward, tech- or literal-German for
 * words that carry a specific business or recruitment meaning in English.
 * Example: "Application" → "Anwendung" (software application) instead of
 * "Bewerbung" (job application). "Administrator" → "Verwalter" (property
 * manager). This module runs a MutationObserver only while the site is set
 * to German and rewrites those bad Google Translate outputs into the correct
 * business German the recruiter industry actually uses.
 *
 * Extend OVERRIDES as more bad translations surface — key is the *bad*
 * German Google produces, value is the *good* recruitment German.
 */

// Ordered case-sensitively; word boundaries prevent accidental substring hits.
// Include noun plurals & lowercase verb forms where relevant.
const OVERRIDES = {
  // ── Platform-wide terminology glossary (2026-08-17) ──────
  // Enforce a single canonical German term for each core concept so different
  // pages don't drift between "Dashboard / Cockpit / Übersichtsseite" etc.
  // Multi-word phrases sit at the top so they beat single-word rules below.
  'Cockpit': 'Übersicht',
  'Startseite': 'Übersicht',
  'Übersichtsseite': 'Übersicht',
  'Dashboard': 'Übersicht',
  'Konfiguration': 'Einstellungen',
  'Optionen': 'Einstellungen',
  'Einrichtung': 'Einstellungen',
  'Nutzerprofil': 'Benutzerprofil',
  'Benutzeraccount': 'Benutzerprofil',
  'Konzerne': 'Unternehmen',
  'Nutzer': 'Benutzer',
  'Anwender': 'Benutzer',
  'Analytik': 'Analysen',
  'Analytics': 'Analysen',
  'Statistiken': 'Analysen',
  'Jobbörse': 'Jobbörsen',
  'Stellenbörse': 'Jobbörsen',
  'Stellenbörsen': 'Jobbörsen',
  'Kalender-Integration': 'Kalenderintegration',
  'Kalender Integration': 'Kalenderintegration',
  'Anschließen': 'Verbinden',
  'Verknüpfen': 'Verbinden',
  'Meldungen': 'Benachrichtigungen',
  'Mitteilungen': 'Benachrichtigungen',
  'Job': 'Stelle',
  'Jobs': 'Stellen',
  'Positionen': 'Stellen',
  'Position': 'Stelle',
  'Anwärter': 'Kandidat',
  'Bewerber-Kandidat': 'Kandidat',
  'Personaler': 'Recruiter',
  'Rekrutierer': 'Recruiter',
  'Publizieren': 'Veröffentlichen',

  // ── Application / Bewerbung ──────────────────────────────
  'Anwendung': 'Bewerbung',
  'Anwendungen': 'Bewerbungen',
  'Antrag': 'Bewerbung',
  'Anträge': 'Bewerbungen',
  'anwenden': 'bewerben',
  'Bewerben': 'Bewerbung', // sometimes Google gives verb noun form
  'anwendung': 'bewerbung',
  'anwendungen': 'bewerbungen',
  'Antragsteller': 'Bewerber',
  'Antragstellerin': 'Bewerberin',

  // ── Administrator / Admin ────────────────────────────────
  // Multi-word phrases go FIRST so they beat the single-word rules below
  // (dictionary sort is longest-first at build time).
  'Recruiter für Verwaltungsangestellte': 'Recruiting-Administrator',
  'Recruiter für Verwaltungsangestellter': 'Recruiting-Administrator',
  'Recruiter für Verwaltungsangestellten': 'Recruiting-Administrator',
  'Recruiter für Administrator': 'Recruiting-Administrator',
  'Administrator Recruiter': 'Recruiting-Administrator',
  'Administrator-Recruiter': 'Recruiting-Administrator',
  'Admin-Recruiter': 'Recruiting-Administrator',
  'Verwaltungsrekrutierer': 'Recruiting-Administrator',
  'Verwalter': 'Administrator',
  'Verwalterin': 'Administratorin',
  'Sachbearbeiter': 'Administrator',
  'Sachbearbeiterin': 'Administratorin',
  'Verwaltung': 'Administration',
  'Verwaltungsangestellte': 'Administrator',
  'Verwaltungsangestellter': 'Administrator',
  'Verwaltungsangestellten': 'Administrator',
  'Verwaltungsassistent': 'Administrator',
  'Verwaltungsassistentin': 'Administratorin',
  'Administrationsassistent': 'Administrator',

  // ── Technology brand names — Google Translate mangles these ─
  'Reagieren': 'React',
  'reagieren': 'React',
  'Typoskript': 'TypeScript',
  'typoskript': 'TypeScript',
  'Knoten.js': 'Node.js',
  'Knoten': 'Node.js',
  'Winkelig': 'Angular',
  'winkelig': 'Angular',
  'winkel': 'Angular',
  'Nächste.js': 'Next.js',
  'Faden': 'Yarn', // "yarn thread"
  'Garn': 'Yarn',

  // ── Dashboard ────────────────────────────────────────────
  'Armaturenbrett': 'Übersicht',
  'Armaturenbretter': 'Übersicht',

  // ── Recruiter / Recruitment ──────────────────────────────
  'Personalbeschaffer': 'Recruiter',
  'Personalbeschafferin': 'Recruiterin',
  'Personalvermittler': 'Recruiter',
  'Personalvermittlerin': 'Recruiterin',
  'Anwerber': 'Recruiter',
  'Anwerberin': 'Recruiterin',
  'Personalbeschaffung': 'Recruiting',
  'Personalvermittlung': 'Recruiting',

  // ── Hiring Manager ───────────────────────────────────────
  'Einstellungsleiter': 'Einstellungsverantwortlicher',
  'Einstellungsleiterin': 'Einstellungsverantwortliche',
  'Einstellungsmanager': 'Einstellungsverantwortlicher',
  'Einstellungsmanagerin': 'Einstellungsverantwortliche',

  // ── Position / Stelle ────────────────────────────────────
  // "Position" is fine in business German; only rewrite the very literal
  // "Stellungnahme"-style mistranslations if they appear.
  'Stellenausschreibung': 'Stellenausschreibung', // no-op — verified good

  // ── Candidate ────────────────────────────────────────────
  // "Kandidat" is fine; guard against Google giving "Bewerber" when a
  // recruiter-facing "Candidate list" is desired — we keep "Kandidat" here.

  // ── Company / Unternehmen ────────────────────────────────
  'Firma': 'Unternehmen',
  'Firmen': 'Unternehmen',

  // ── Sourcing (loanword preferred by recruiters) ──────────
  'Beschaffung': 'Sourcing',
  'Direktansprache': 'Sourcing',

  // ── Interview stages ─────────────────────────────────────
  'Vor-Ort-Vorstellungsgespräch': 'Onsite-Interview',
  'Vor-Ort-Interview': 'Onsite-Interview',
  'Präsenzvorstellungsgespräch': 'Onsite-Interview',
  'Präsenzinterview': 'Onsite-Interview',
  'Teammeeting': 'Onsite-Interview',
  'Team-Meeting': 'Onsite-Interview',
  'Teambesprechung': 'Onsite-Interview',
  'Onsite Interview': 'Onsite-Interview', // hyphenate for German compound-word feel
  'Vorstellungsgespräch': 'Interview',
  'Vorstellungsgespräche': 'Interviews',

  // ── Screening ────────────────────────────────────────────
  'Vorprüfung': 'Screening',
  'Sichtung': 'Screening',

  // ── Recruiting terminology (Google Translate mangles these) ───────
  // "Match Score" → GT sometimes gives "Spielergebnis" (soccer score).
  // Universally keep the industry term "Matching Score".
  'Spielergebnis': 'Matching Score',
  'Spielergebnisse': 'Matching Score',
  'Spielstand': 'Matching Score',
  'Übereinstimmungsergebnis': 'Matching Score',
  // "Match Report" → GT gives "Spielbericht". Our product-named artefact is
  // "Jobasti Synopsis" everywhere.
  'Spielbericht': 'Jobasti Synopsis',
  'Spielberichte': 'Jobasti Synopsis',
  'Spielberichts': 'Jobasti Synopsis',

  // ── Members section ──────────────────────────────────────────────
  // "Organigram / Org chart" → GT gives "Organigramm" (implies hierarchy).
  // We just group by role — "Teamübersicht" reads correctly.
  'Organigramm': 'Teamübersicht',
  'Organigramms': 'Teamübersicht',

  // ── Billing (invoices) ───────────────────────────────────────────
  // "Amount" (money) → GT sometimes gives "Menge" (quantity/count).
  // In our context the column is always a money amount → "Betrag".
  'Menge': 'Betrag',

  // ── Work arrangement (Remote / Hybrid / On-site) ─────────
  // Google Translate renders "Remote" as "Fernbedienung" (TV remote control)
  // or "Fernsteuerung" — completely wrong in a recruiting context. Recruiters
  // in the DACH region universally keep the English loanword "Remote".
  'Fernbedienung': 'Remote',
  'Fernbedienungen': 'Remote',
  'Fernbedient': 'Remote',
  'Fernsteuerung': 'Remote',
  'Ferngesteuert': 'Remote',
  'Fernarbeit': 'Remote',
  'Telearbeit': 'Remote',
  'Heimarbeit': 'Remote',
  // "On-site" → GT sometimes gives "Vor Ort" (fine) or "Am Standort" (fine),
  // but occasionally "Auf der Baustelle" (construction site). Guard that.
  'Auf der Baustelle': 'Vor Ort',
  'Baustelle': 'Vor Ort',
};

// Build a single case-sensitive union regex once. Word-boundary aware.
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const keys = Object.keys(OVERRIDES).sort((a, b) => b.length - a.length); // longer first
const UNION_RE = keys.length
  ? new RegExp(`(?<![A-Za-zÄÖÜäöüß])(${keys.map(escapeRe).join('|')})(?![A-Za-zÄÖÜäöüß])`, 'g')
  : null;

let observer = null;
let active = false;
let sweepTimer = null;
let inSelfWrite = false;

const isGermanActive = () => {
  // 1) User's explicitly-picked language (source of truth)
  try {
    const ls = localStorage.getItem('jobasti_lang');
    if (ls && ls.toLowerCase().startsWith('de')) return true;
  } catch { /* noop */ }
  // 2) Cookie fallback (same key)
  try {
    const m = document.cookie.match(/(?:^|;\s*)jobasti_lang=([^;]+)/);
    if (m && decodeURIComponent(m[1]).toLowerCase().startsWith('de')) return true;
  } catch { /* noop */ }
  // 3) Google Translate cookie (/en/de) or its <html> class
  try {
    const m = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
    if (m && /\/de\b/.test(decodeURIComponent(m[1]))) return true;
  } catch { /* noop */ }
  const html = document.documentElement;
  const lang = (html.getAttribute('lang') || '').toLowerCase();
  if (lang.startsWith('de')) return true;
  if (html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl')) {
    // Google Translate is active — assume it's DE since we only override for DE
    // and non-DE targets are handled elsewhere. Safe: our replacements only
    // touch specific German words that don't exist in English or other langs.
    return true;
  }
  return false;
};

const replaceInTextNode = (node) => {
  if (!UNION_RE) return;
  const t = node.nodeValue;
  if (!t || t.length < 4) return;
  UNION_RE.lastIndex = 0;
  if (!UNION_RE.test(t)) return;
  UNION_RE.lastIndex = 0;
  const out = t.replace(UNION_RE, (m) => OVERRIDES[m] || m);
  if (out !== t) {
    inSelfWrite = true;
    node.nodeValue = out;
    inSelfWrite = false;
  }
};

const walk = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const p = n.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      const tag = p.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA') return NodeFilter.FILTER_REJECT;
      // Honour explicit opt-outs from author code / Google Translate itself.
      if (p.closest('[translate="no"], .notranslate, .skiptranslate')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) replaceInTextNode(n);
};

const scheduleSweep = () => {
  if (!active) return;
  clearTimeout(sweepTimer);
  sweepTimer = setTimeout(() => walk(document.body), 140);
};

const onMutations = (mutations) => {
  if (inSelfWrite) return;
  for (const m of mutations) {
    if (m.type === 'characterData' || m.type === 'childList') { scheduleSweep(); return; }
  }
};

const attach = () => {
  if (observer) return;
  walk(document.body);
  observer = new MutationObserver(onMutations);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
};

const detach = () => {
  if (observer) { observer.disconnect(); observer = null; }
  clearTimeout(sweepTimer);
};

const evaluate = () => {
  const shouldBeActive = isGermanActive();
  if (shouldBeActive && !active) { active = true; attach(); }
  else if (!shouldBeActive && active) { active = false; detach(); }
};

/**
 * Wire the observer at app boot. Idempotent.
 */
export const initGermanBusinessLingo = () => {
  if (typeof document === 'undefined') return;
  // React to Google-Translate flipping <html lang> or its own class swap.
  new MutationObserver(evaluate).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['lang', 'class'],
  });
  evaluate();
};
